import React, { useEffect, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import qs from "query-string";
import { toast } from "react-toastify";
import {
  Button,
  Label,
  Input,
  Row,
  Col,
  Container,
  CardHeader,
  CardBody,
  Card,
  Tooltip,
} from "reactstrap";
import api from "service/api";
import qualityApi from "service/qualityApi";

const SLAConfigurationForm = () => {
  const { handleSubmit, reset, setValue, control } = useForm({
    defaultValues: {
      client_id: "",
      challengeDays: "",
      challengeType: "corridos",
      eventDays: "",
      eventType: "corridos",
    },
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const getParamsId = useCallback(() => {
    const { id } = qs.parse(window.location.search);
    return id || undefined;
  }, []);

  useEffect(() => {
    const getClients = async () => {
      try {
        const response = await api.get("/clients");
        setClients(
          response.data.clients.map((client) => ({
            value: client.id,
            label: client.name,
          })),
        );
      } catch (error) {
        toast.error("Erro ao recuperar clientes!");
      }
    };
    getClients();
  }, []);

  useEffect(() => {
    const fetchSlaConfig = async () => {
      if (clients.length > 0 && getParamsId()) {
        try {
          const response = await qualityApi.get(
            `/sla/configuration/${getParamsId()}`,
          );
          const { client, config } = response.data;
          const clientSla = clients.find((c) => c.value === client.id);
          setSelectedClient(clientSla || null);
          setValue("client_id", client.id || "");
          setValue("challengeDays", config?.timeToContestation?.maxDays || "");
          setValue(
            "challengeType",
            config?.timeToContestation?.businessDays ? "úteis" : "corridos",
          );
          setValue("eventDays", config?.timeToEvaluableEvent?.maxDays || "");
          setValue(
            "eventType",
            config?.timeToEvaluableEvent?.businessDays ? "úteis" : "corridos",
          );
        } catch (error) {
          toast.error("Erro ao recuperar configuração de SLA!");
        }
      } else if (!getParamsId()) {
        reset({
          client_id: "",
          challengeDays: "",
          challengeType: "corridos",
          eventDays: "",
          eventType: "corridos",
        });
        setSelectedClient(null);
      }
    };
    fetchSlaConfig();
  }, [clients, setValue, reset, getParamsId]);

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const onSubmit = async (payload) => {
    const id = getParamsId();
    if (id) {
      await qualityApi.patch(`/sla/configuration/${id}`, payload);
      toast.success(
        "Configuração de janela de monitoria atualizada com sucesso!",
      );
    } else {
      await qualityApi.post("/sla/configuration", payload);
      toast.success(
        "Configuração de janela de monitoria cadastrada com sucesso!",
      );
    }
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    const payload = {
      clientId: selectedClient?.value || "",
      config: {
        timeToContestation: {
          maxDays: Number(formData.challengeDays),
          businessDays: formData.challengeType === "úteis",
        },
        timeToEvaluableEvent: {
          maxDays: Number(formData.eventDays),
          businessDays: formData.eventType === "úteis",
        },
      },
    };
    try {
      await onSubmit(payload);
    } catch (e) {
      if (e.response && e.response.status === 400) {
        toast.error("Erro ao salvar configuração de janela de monitoria.");
      } else if (e.response && e.response.status === 409) {
        toast.error(
          "Já existe uma configuração de janela de monitoria para este cliente.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-7" fluid>
      <Col xl="8">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0 d-flex align-items-center">
                  {getParamsId()
                    ? "Editar Janela de Monitoria"
                    : "Cadastrar Janela de Monitoria"}
                  <i
                    className="fas fa-info-circle ml-2"
                    id="janelaMonitoriaInfo"
                    style={{
                      color: "#5e72e4",
                      cursor: "pointer",
                      fontSize: 20,
                    }}
                  />
                  <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target="janelaMonitoriaInfo"
                    toggle={toggleTooltip}
                  >
                    Esta configuração de janela de monitoria define o tempo
                    máximo permitido para realizar ações após um atendimento ou
                    evento ser analisado. Você pode escolher se o prazo será
                    contado em dias úteis ou corridos. Após o vencimento desse
                    período, as operações relacionadas ao atendimento ficarão
                    bloqueadas, impedindo novas ações. Essa configuração garante
                    maior controle e organização dos prazos dentro do processo
                    de monitoria.
                  </Tooltip>
                </h3>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="pl-lg-4">
                <Row className="align-items-center mb-1">
                  <Col md="8" sm="12">
                    <Label for="client_id">Cliente</Label>
                    <Select
                      id="client-select"
                      options={clients.sort((a, b) =>
                        a.label.localeCompare(b.label),
                      )}
                      value={selectedClient}
                      onChange={(selectedOption) =>
                        setSelectedClient({
                          value: selectedOption.value,
                          label: selectedOption.label,
                        })
                      }
                      placeholder="Selecione um cliente"
                      required
                      isDisabled={!!getParamsId()}
                    />
                  </Col>
                </Row>
                <hr className="my-4" />
                <Row className="align-items-center mb-3">
                  <Col md="4" sm="12" className="d-flex align-items-center">
                    <Label
                      for="challengeDays"
                      className="mb-0 mr-2"
                      style={{ minWidth: 180 }}
                    >
                      Tempo para Contestação
                    </Label>
                    <Controller
                      name="challengeDays"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          type="text"
                          id="challengeDays"
                          maxLength={3}
                          required
                          style={{
                            width: 60,
                            textAlign: "center",
                            marginRight: 8,
                          }}
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <span className="mr-2">dias</span>
                    <Controller
                      name="challengeType"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          type="select"
                          id="challengeType"
                          required
                          style={{ width: 100 }}
                          {...field}
                          value={field.value || "corridos"}
                        >
                          <option value="corridos">Corridos</option>
                          <option value="úteis">Úteis</option>
                        </Input>
                      )}
                    />
                  </Col>
                </Row>
                <Row className="align-items-center mb-3">
                  <Col md="4" sm="12" className="d-flex align-items-center">
                    <Label
                      for="eventDays"
                      className="mb-0 mr-2"
                      style={{ minWidth: 180 }}
                    >
                      Tempo para Evento Avaliável
                    </Label>
                    <Controller
                      name="eventDays"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          type="text"
                          id="eventDays"
                          maxLength={3}
                          required
                          style={{
                            width: 60,
                            textAlign: "center",
                            marginRight: 8,
                          }}
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <span className="mr-2">dias</span>
                    <Controller
                      name="eventType"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          type="select"
                          id="eventType"
                          required
                          style={{ width: 100 }}
                          {...field}
                          value={field.value || "corridos"}
                        >
                          <option value="corridos">Corridos</option>
                          <option value="úteis">Úteis</option>
                        </Input>
                      )}
                    />
                  </Col>
                </Row>
              </div>
              <hr className="my-4" />
              <Button type="submit" color="primary" disabled={loading}>
                {getParamsId() ? "Atualizar" : "Adicionar"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Container>
  );
};

export default SLAConfigurationForm;
